import type { NotificationResponse } from "@/api";

/** Sentence keys in notifications.json ("sentence.<key>"). */
export type SentenceKey =
  | "chatShared"
  | "brainstormShared"
  | "researchShared"
  | "taskShared"
  | "chatSharedUntitled"
  | "brainstormSharedUntitled"
  | "researchSharedUntitled"
  | "taskSharedUntitled"
  | "flowCompleted"
  | "flowFailed"
  | "flowPaused"
  | "flowMessage"
  | "flowScheduled"
  | "taskCompleted"
  | "taskFailed"
  | "syncedCount"
  | "syncedDone"
  | "syncFailed"
  | "syncReauth";

export interface Sentence {
  key: SentenceKey;
  actor: string;
  subject: string;
  count?: number;
}

type Display = Pick<NotificationResponse, "type" | "metadata" | "actor" | "subject">;

const SHARED: Record<string, SentenceKey> = {
  chat_shared: "chatShared",
  brainstorm_shared: "brainstormShared",
  deep_research_shared: "researchShared",
  task_shared: "taskShared",
};

const ABOUT_SUBJECT: Record<string, SentenceKey> = {
  flow_completed: "flowCompleted",
  flow_failed: "flowFailed",
  flow_paused: "flowPaused",
  scheduled_flow_triggered: "flowScheduled",
  task_completed: "taskCompleted",
  task_failed: "taskFailed",
  data_sync_failed: "syncFailed",
};

function subjectKey(n: Display): SentenceKey | undefined {
  const m = n.metadata ?? {};
  if (n.type === "flow_completed" && m.source === "flow_step") return "flowMessage";
  if (n.type === "data_sync_failed" && m.reason === "reauthorization_required") return "syncReauth";
  if (n.type === "data_sync_completed") return typeof m.document_count === "number" ? "syncedCount" : "syncedDone";
  return ABOUT_SUBJECT[n.type];
}

/**
 * The structured sentence for a notification, or null when it lacks the
 * parts (older notifications), in which case the item shows title + message.
 */
export function describeNotification(n: Display): Sentence | null {
  const subject = n.subject?.name;
  const shared = SHARED[n.type];
  if (shared) {
    if (!n.actor) return null;
    // An untitled chat still reads as "<person> shared a chat with you".
    const key = subject ? shared : (`${shared}Untitled` as SentenceKey);
    return { key, actor: n.actor.name, subject: subject ?? "" };
  }
  if (!subject) return null;
  const key = subjectKey(n);
  if (!key) return null;
  const count = key === "syncedCount" ? (n.metadata?.document_count as number) : undefined;
  return { key, actor: n.actor?.name ?? "", subject, count };
}

export type NotificationIcon = "workflow" | "task" | "knowledge" | "share" | "agent" | "bell";

export function iconFor(n: Pick<NotificationResponse, "type" | "actor">): NotificationIcon {
  if (n.actor?.kind === "agent") return "agent";
  if (n.type.startsWith("flow_") || n.type === "scheduled_flow_triggered") return "workflow";
  if (n.type.startsWith("task_")) return "task";
  if (n.type.startsWith("data_sync_")) return "knowledge";
  if (n.type.endsWith("_shared")) return "share";
  return "bell";
}

/** Failures are the one case the icon tile carries status color. */
export const isFailure = (type: string) => type.endsWith("_failed");
